import React, { useEffect, useRef, useState } from 'react'

import classnames from 'classnames'

import DropdownArrow from './DropdownArrow'

type ConsumerProps = {
  close: () => void
  ref?: React.RefObject<any>
}

type Props = {
  disabled?: boolean
  opener?: (
    toggle: (e: React.SyntheticEvent) => void,
    expanded: boolean
  ) => JSX.Element
  label: React.ReactNode
  className?: string
  withBorder?: boolean
  children: ((t: ConsumerProps) => React.ReactNode) | React.ReactNode
  leftAligned?: boolean
  hideArrow?: boolean
  style?: React.CSSProperties
  describedBy?: string
  testId?: string
  labelledBy?: string
  ariaDescription?: string
  ariaLabel?: string
  contentRole?: string
  id?: string
}

const DropDown = (props: Props) => {
  const [expanded, setExpanded] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const clickInsideContentRef = useRef(false)

  // Close this dropdown when the user clicks anywhere outside its wrapper.
  // onBlur alone is unreliable: clicking another filter or a non-focusable
  // page element doesn't always move focus, so the dropdown can stay open.
  useEffect(() => {
    if (!expanded) {
      return
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setExpanded(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [expanded])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      toggle(e)
    }
  }

  const toggle = (e: React.SyntheticEvent) => {
    if (props.disabled) {
      return
    }

    e.stopPropagation()
    e.preventDefault()
    setExpanded((expanded) => !expanded)
  }

  const close = () => {
    setExpanded(false)
    ref.current?.focus()
  }

  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    // safari workaround, where relatedTarget is null when clicking inside the dropdown
    if (clickInsideContentRef.current) {
      clickInsideContentRef.current = false
      return
    }
    // Check if focus moved to the dropdown content or stayed within the dropdown
    const wrapper = event.currentTarget.parentElement
    if (wrapper && !wrapper.contains(event.relatedTarget as Node)) {
      setExpanded(false)
    }
  }

  const handleContentMouseDown = () => {
    clickInsideContentRef.current = true
  }

  return (
    <div className='dropDownWrapper' ref={wrapperRef}>
      <div
        id={props.id}
        ref={ref}
        className={classnames('dropDown', props.className, {
          withBorder: props.withBorder,
          disabled: props.disabled
        })}
        {...(props.ariaLabel ? { 'aria-label': props.ariaLabel } : {})}
        {...(props.describedBy
          ? { 'aria-describedby': props.describedBy }
          : {})}
        {...(props.labelledBy ? { 'aria-labelledby': props.labelledBy } : {})}
        {...(props.ariaDescription
          ? { 'aria-description': props.ariaDescription }
          : {})}
        role='button'
        aria-controls={`${
          props.testId || 'dropdown-selector'
        }-dropdown-content`}
        aria-haspopup={props.disabled ? undefined : 'menu'}
        aria-expanded={expanded}
        data-testid={props.testId ?? 'dropdown-selector'}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        onClick={toggle}
      >
        {(props.opener && props.opener(toggle, expanded)) || (
          <>
            <div
              className='dropDownLabel'
              onClick={toggle}
              style={props.style}
              data-testid={`${props.testId ?? 'dropdown-selector'}-label`}
            >
              {props.label}
            </div>
            {!props.hideArrow && (
              <div onClick={toggle} className='arrow'>
                <DropdownArrow expanded={expanded} />
              </div>
            )}
          </>
        )}
      </div>
      <div
        id={`${props.testId}-dropdown-content`}
        className={classnames(
          'dropDownContent',
          !expanded && 'hide',
          props.leftAligned && 'leftAligned'
        )}
        data-testid={
          props.testId ? `${props.testId}-dropdown-content` : 'dropdown-content'
        }
        onMouseDown={handleContentMouseDown}
        onClick={(e) => {
          e.stopPropagation()
          close()
        }}
        onBlur={onBlur}
        role={props.contentRole || 'listbox'}
      >
        {typeof props.children === 'function'
          ? props.children({ close, ref })
          : props.children}
      </div>
    </div>
  )
}

export default DropDown
